import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input, Textarea, SearchableSelect, InputAmount, Badge } from '@/backoffice/components/ui';
import { formatCurrency } from '@/backoffice/utils';
import { fetchZones } from '@/domains/admin/services/admin.service';
import { listDeliveryTypes, listPaymentMethods } from '@/domains/api/delivery.api';
import { listUsersByType } from '@/domains/api/type-users.api';
import { listAllAccountTypes } from '@/domains/api/configuration.api';
import { createOrder } from '@/domains/client/services/client.service';
import type { ApiDeliveryType, ApiPaymentMethod, ApiUser } from '@/domains/api/types';
import {
  Package,
  MapPin,
  Truck,
  Users,
  Shield,
  ChevronRight,
  ChevronLeft,
  Check,
  Info,
  AlertCircle,
  Loader2,
  Search,
} from 'lucide-react';

type Step = 'type' | 'pickup' | 'dropoff' | 'details' | 'payment';
type DriverTypeKey = 'professional' | 'community';

const DRIVER_TYPE_MAP: Record<DriverTypeKey, string> = {
  professional: 'professional_delivery',
  community: 'community_delivery',
};

const driverTypes = [
  {
    id: 'professional' as DriverTypeKey,
    label: 'Livreur Professionnel',
    description: 'Coursier dédié et assuré',
    features: ['Suivi GPS en temps réel', 'Assurance complète', 'Support 24/7'],
  },
  {
    id: 'community' as DriverTypeKey,
    label: 'Livreur Communautaire',
    description: 'Voisin de confiance',
    features: ['Partage de coûts réduit', 'Lien communautaire', 'Flexible'],
  },
];

const steps: { key: Step; label: string }[] = [
  { key: 'type', label: 'Type' },
  { key: 'pickup', label: 'Collecte' },
  { key: 'dropoff', label: 'Livraison' },
  { key: 'details', label: 'Colis' },
  { key: 'payment', label: 'Confirmation' },
];

function toNumber(value: number | string | null | undefined): number {
  if (value == null) return 0;
  return typeof value === 'number' ? value : parseFloat(value) || 0;
}

export function NewOrder() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('type');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capacityError, setCapacityError] = useState<string | null>(null);

  const [deliveryTypes, setDeliveryTypes] = useState<ApiDeliveryType[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<ApiPaymentMethod[]>([]);
  const [zones, setZones] = useState<Array<{ id: number; name: string }>>([]);
  const [typeIds, setTypeIds] = useState<Record<DriverTypeKey, number | null>>({
    professional: null,
    community: null,
  });

  const [availableDrivers, setAvailableDrivers] = useState<ApiUser[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [driverSearch, setDriverSearch] = useState('');

  const [formData, setFormData] = useState({
    deliveryTypeId: null as number | null,
    driverType: '' as DriverTypeKey | '',
    driverId: null as number | null,
    departureZoneId: null as number | null,
    arrivalZoneId: null as number | null,
    departureAddress: '',
    arrivalAddress: '',
    departureLandmark: '',
    arrivalLandmark: '',
    packageDescription: '',
    packageWeight: 1,
    packageVolume: 0,
    declaredValue: 0,
    needsInsurance: false,
    notes: '',
    payNow: false,
    paymentMethodId: null as number | null,
  });

  const zoneOptions = zones.map((z) => ({ value: String(z.id), label: z.name }));

  const selectedDeliveryType = deliveryTypes.find((t) => t.id === formData.deliveryTypeId);
  const selectedDriver = availableDrivers.find((d) => d.id === formData.driverId);

  const estimatedPrice = selectedDeliveryType
    ? toNumber(selectedDeliveryType.amount_per_kg) * formData.packageWeight
    : 0;

  const loadDrivers = useCallback(async (driverType: DriverTypeKey, search?: string) => {
    const typeId = typeIds[driverType];
    if (!typeId) return;
    setLoadingDrivers(true);
    try {
      const drivers = await listUsersByType(typeId, {
        status: 'active',
        search: search?.trim() || undefined,
      });
      setAvailableDrivers(drivers);
    } catch {
      setAvailableDrivers([]);
      setError('Impossible de charger les livreurs disponibles.');
    } finally {
      setLoadingDrivers(false);
    }
  }, [typeIds]);

  useEffect(() => {
    async function loadCatalog() {
      setLoading(true);
      try {
        const [dt, pm, z, types] = await Promise.all([
          listDeliveryTypes(),
          listPaymentMethods(),
          fetchZones(),
          listAllAccountTypes(),
        ]);
        setDeliveryTypes(dt);
        setPaymentMethods(pm);
        setZones(z);
        const proType = types.find((t) => t.name === DRIVER_TYPE_MAP.professional);
        const comType = types.find((t) => t.name === DRIVER_TYPE_MAP.community);
        setTypeIds({
          professional: proType?.id ?? null,
          community: comType?.id ?? null,
        });
        if (pm.length > 0) {
          setFormData((prev) => ({ ...prev, paymentMethodId: pm[0].id }));
        }
      } catch {
        setError('Impossible de charger les données de commande.');
      } finally {
        setLoading(false);
      }
    }
    loadCatalog();
  }, []);

  useEffect(() => {
    if (formData.driverType) {
      setFormData((prev) => ({ ...prev, driverId: null }));
      setDriverSearch('');
      loadDrivers(formData.driverType);
    } else {
      setAvailableDrivers([]);
    }
  }, [formData.driverType, loadDrivers]);

  const validateCapacity = (): string | null => {
    if (!selectedDriver?.vehicle_capacity) return null;
    const maxWeight = toNumber(selectedDriver.vehicle_capacity.max_weight);
    const maxVolume = toNumber(selectedDriver.vehicle_capacity.max_volume);
    if (formData.packageWeight > maxWeight) {
      return `Le poids (${formData.packageWeight} kg) dépasse la capacité du livreur (${maxWeight} kg).`;
    }
    if (formData.packageVolume > 0 && maxVolume > 0 && formData.packageVolume > maxVolume) {
      return `Le volume (${formData.packageVolume} m³) dépasse la capacité du livreur (${maxVolume} m³).`;
    }
    return null;
  };

  const currentStepIndex = steps.findIndex((s) => s.key === step);

  const canProceed = () => {
    switch (step) {
      case 'type':
        return formData.deliveryTypeId && formData.driverType && formData.driverId;
      case 'pickup':
        return formData.departureZoneId && formData.departureAddress.trim();
      case 'dropoff':
        return formData.arrivalZoneId && formData.arrivalAddress.trim();
      case 'details':
        return formData.packageDescription.trim() && formData.packageWeight > 0 && !validateCapacity();
      case 'payment':
        return formData.payNow ? formData.paymentMethodId != null : true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (step === 'details') {
      const capErr = validateCapacity();
      if (capErr) {
        setCapacityError(capErr);
        return;
      }
      setCapacityError(null);
    }
    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex < steps.length) {
      setStep(steps[nextStepIndex].key);
    }
  };

  const handleSubmit = async () => {
    if (!formData.deliveryTypeId || !formData.departureZoneId || !formData.arrivalZoneId) {
      return;
    }
    if (formData.payNow && !formData.paymentMethodId) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await createOrder({
        delivery_type_id: formData.deliveryTypeId,
        driver_id: formData.driverId,
        departure_zone_id: formData.departureZoneId,
        arrival_zone_id: formData.arrivalZoneId,
        departure_address: formData.departureAddress.trim(),
        arrival_address: formData.arrivalAddress.trim(),
        departure_landmark: formData.departureLandmark.trim() || null,
        arrival_landmark: formData.arrivalLandmark.trim() || null,
        package_description: [formData.packageDescription.trim(), formData.notes.trim()].filter(Boolean).join('\n'),
        estimated_weight: formData.packageWeight,
        estimated_volume: formData.packageVolume > 0 ? formData.packageVolume : null,
        declared_value: formData.declaredValue,
        pay_now: formData.payNow,
        payment_method_id: formData.payNow ? formData.paymentMethodId : null,
        total_delivery_fee: estimatedPrice + (formData.needsInsurance ? 2500 : 0),
      });
      if (formData.payNow) {
        const isMobileMoney = paymentMethods.find((p) => p.id === formData.paymentMethodId)
          ?.name.toLowerCase().includes('mobile');
        if (isMobileMoney) {
          navigate(`/dashboard/client/commandes/${created.id}/paiement`);
          return;
        }
      }
      navigate('/dashboard/client/commandes');
    } catch {
      setError('Échec de la création de la commande. Vérifiez vos informations.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Chargement...
        </div>
      );
    }

    switch (step) {
      case 'type':
        return (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Type de livraison</h3>
              {deliveryTypes.length === 0 ? (
                <p className="text-sm text-amber-600">Aucun type de livraison configuré. Contactez l'administrateur.</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {deliveryTypes.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, deliveryTypeId: type.id })}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                        formData.deliveryTypeId === type.id
                          ? 'bg-teal-50 border-teal-400'
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          formData.deliveryTypeId === type.id ? 'text-teal-600' : 'text-gray-400'
                        }`}>
                          <Package className="w-8 h-8" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{type.name}</h4>
                          <p className="text-sm text-gray-500">{type.description || 'Livraison standard'}</p>
                          <Badge variant="primary" size="sm" className="mt-1">
                            {toNumber(type.amount_per_kg)} GNF/kg
                          </Badge>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Choisir un livreur</h3>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {driverTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, driverType: type.id, driverId: null })}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                      formData.driverType === type.id
                        ? 'bg-blue-50 border-blue-400'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${
                        formData.driverType === type.id
                          ? 'bg-blue-100 text-blue-600'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {type.id === 'professional' ? <Truck className="w-6 h-6" /> : <Users className="w-6 h-6" />}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{type.label}</h4>
                        <p className="text-sm text-gray-500">{type.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-1">
                      {type.features.map((feature, i) => (
                        <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>

              {formData.driverType && (
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={driverSearch}
                        onChange={(e) => setDriverSearch(e.target.value)}
                        placeholder="Rechercher un livreur..."
                        className="pl-9"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadDrivers(formData.driverType as DriverTypeKey, driverSearch)}
                    >
                      Rechercher
                    </Button>
                  </div>

                  {loadingDrivers ? (
                    <div className="flex items-center gap-2 text-gray-500 py-4">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Recherche des livreurs disponibles...
                    </div>
                  ) : availableDrivers.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4">
                      Aucun livreur {formData.driverType === 'professional' ? 'professionnel' : 'communautaire'} disponible.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {availableDrivers.map((driver) => {
                        const capacity = driver.vehicle_capacity;
                        const isSelected = formData.driverId === driver.id;
                        return (
                          <button
                            key={driver.id}
                            type="button"
                            onClick={() => setFormData({ ...formData, driverId: driver.id })}
                            className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                              isSelected
                                ? 'bg-teal-50 border-teal-400'
                                : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {driver.firstnames} {driver.lastname}
                                </p>
                                <p className="text-sm text-gray-500">{driver.phone}</p>
                                {driver.zone && (
                                  <p className="text-xs text-gray-400 mt-0.5">Zone : {driver.zone.name}</p>
                                )}
                              </div>
                              {capacity && (
                                <div className="text-right text-xs text-gray-500">
                                  <p>Max {toNumber(capacity.max_weight)} kg</p>
                                  {toNumber(capacity.max_volume) > 0 && (
                                    <p>Max {toNumber(capacity.max_volume)} m³</p>
                                  )}
                                </div>
                              )}
                              {isSelected && <Check className="w-5 h-5 text-teal-600 ml-2" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 'pickup':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">Adresse de collecte</p>
                <p className="text-sm text-blue-700">C'est l'endroit où le livreur prendra en charge votre colis.</p>
              </div>
            </div>

            <SearchableSelect
              label="Zone de départ"
              options={zoneOptions}
              value={formData.departureZoneId ? String(formData.departureZoneId) : ''}
              onChange={(v) => setFormData({ ...formData, departureZoneId: v ? parseInt(v, 10) : null })}
              placeholder="Sélectionner la zone"
            />

            <Input
              label="Adresse précise"
              value={formData.departureAddress}
              onChange={(e) => setFormData({ ...formData, departureAddress: e.target.value })}
              placeholder="Ex: Rue principale, près de la pharmacie"
            />

            <Input
              label="Point de repère (optionnel)"
              value={formData.departureLandmark}
              onChange={(e) => setFormData({ ...formData, departureLandmark: e.target.value })}
              placeholder="Ex: En face de la station Shell"
              hint="Aidez le livreur à vous trouver plus facilement"
            />
          </div>
        );

      case 'dropoff':
        return (
          <div className="space-y-6">
            <div className="bg-emerald-50 p-4 rounded-lg flex items-start gap-3">
              <MapPin className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="text-sm text-emerald-800 font-medium">Adresse de livraison</p>
                <p className="text-sm text-emerald-700">C'est l'endroit où le colis sera déposé.</p>
              </div>
            </div>

            <SearchableSelect
              label="Zone d'arrivée"
              options={zoneOptions}
              value={formData.arrivalZoneId ? String(formData.arrivalZoneId) : ''}
              onChange={(v) => setFormData({ ...formData, arrivalZoneId: v ? parseInt(v, 10) : null })}
              placeholder="Sélectionner la zone"
            />

            <Input
              label="Adresse précise"
              value={formData.arrivalAddress}
              onChange={(e) => setFormData({ ...formData, arrivalAddress: e.target.value })}
              placeholder="Ex: Rue commerciale, immeuble Bleu"
            />

            <Input
              label="Point de repère (optionnel)"
              value={formData.arrivalLandmark}
              onChange={(e) => setFormData({ ...formData, arrivalLandmark: e.target.value })}
              placeholder="Ex: Près du marché central"
            />
          </div>
        );

      case 'details':
        return (
          <div className="space-y-6">
            <Textarea
              label="Description du colis"
              value={formData.packageDescription}
              onChange={(e) => setFormData({ ...formData, packageDescription: e.target.value })}
              placeholder="Décrivez le contenu de votre colis..."
            />

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Poids estimé (kg)"
                type="number"
                min="0.1"
                step="0.1"
                value={formData.packageWeight}
                onChange={(e) => {
                  setCapacityError(null);
                  setFormData({ ...formData, packageWeight: parseFloat(e.target.value) || 0 });
                }}
                placeholder="Ex: 2.5"
              />
              <Input
                label="Volume estimé (m³)"
                type="number"
                min="0"
                step="0.01"
                value={formData.packageVolume || ''}
                onChange={(e) => {
                  setCapacityError(null);
                  setFormData({ ...formData, packageVolume: parseFloat(e.target.value) || 0 });
                }}
                placeholder="Ex: 0.05"
              />
            </div>

            {selectedDriver?.vehicle_capacity && (
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                Capacité du livreur sélectionné :{' '}
                <strong>{toNumber(selectedDriver.vehicle_capacity.max_weight)} kg</strong>
                {toNumber(selectedDriver.vehicle_capacity.max_volume) > 0 && (
                  <> / <strong>{toNumber(selectedDriver.vehicle_capacity.max_volume)} m³</strong></>
                )}
              </div>
            )}

            {capacityError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {capacityError}
              </div>
            )}

            <InputAmount
              label="Valeur déclarée (optionnel)"
              value={formData.declaredValue}
              onChange={(v) => setFormData({ ...formData, declaredValue: v })}
              hint="Pour les colis de valeur, nous recommandons d'activer l'assurance"
            />

            {formData.declaredValue > 50000 && (
              <Card className="bg-amber-50 border-amber-200">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Assurance recommandée</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Pour une valeur déclarée supérieure à 50 000 GNF, nous recommandons notre assurance colis à 2 500 GNF.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => setFormData({ ...formData, needsInsurance: !formData.needsInsurance })}
                    >
                      {formData.needsInsurance ? 'Assurance activée' : 'Activer l\'assurance'}
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            <Textarea
              label="Instructions supplémentaires (optionnel)"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Ex: Fragile, à remettre en main propre..."
            />
          </div>
        );

      case 'payment':
        return (
          <div className="space-y-6">
            <Card>
              <h3 className="font-semibold text-gray-900 mb-4">Récapitulatif</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Type de livraison</span>
                  <span className="font-medium">{selectedDeliveryType?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Livreur</span>
                  <span className="font-medium">
                    {selectedDriver
                      ? `${selectedDriver.firstnames} ${selectedDriver.lastname}`
                      : formData.driverType === 'professional' ? 'Professionnel' : 'Communautaire'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Départ</span>
                  <span className="font-medium">
                    {zones.find((z) => z.id === formData.departureZoneId)?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Arrivée</span>
                  <span className="font-medium">
                    {zones.find((z) => z.id === formData.arrivalZoneId)?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Poids</span>
                  <span className="font-medium">{formData.packageWeight} kg</span>
                </div>
                {formData.packageVolume > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Volume</span>
                    <span className="font-medium">{formData.packageVolume} m³</span>
                  </div>
                )}
                {formData.needsInsurance && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Assurance</span>
                    <span className="font-medium">2 500 GNF</span>
                  </div>
                )}
                <div className="border-t pt-3 flex justify-between">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="text-xl font-bold text-teal-600">
                    {formatCurrency(estimatedPrice + (formData.needsInsurance ? 2500 : 0))}
                  </span>
                </div>
              </div>
            </Card>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Paiement</h3>
              <p className="text-sm text-gray-500 mb-4">
                Les zones (Kaloum, Ratoma…) servent au positionnement sur la carte ; le GPS du livreur est en temps réel.
              </p>

              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payNow: true })}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    formData.payNow
                      ? 'bg-blue-50 border-blue-400'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-medium text-gray-900">Payer maintenant</p>
                  <p className="text-sm text-gray-500 mt-1">Choisissez un mode de paiement et réglez dès la création</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payNow: false, paymentMethodId: null })}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    !formData.payNow
                      ? 'bg-blue-50 border-blue-400'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-medium text-gray-900">Payer à la livraison</p>
                  <p className="text-sm text-gray-500 mt-1">Confirmez et payez une fois le colis livré</p>
                </button>
              </div>

              {formData.payNow && (
                <>
                  <h4 className="font-medium text-gray-900 mb-3">Mode de paiement</h4>
                  {paymentMethods.length === 0 ? (
                    <p className="text-sm text-amber-600">Aucun mode de paiement configuré.</p>
                  ) : (
                    <div className="grid gap-3">
                      {paymentMethods.map((method) => (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, paymentMethodId: method.id })}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            formData.paymentMethodId === method.id
                              ? 'bg-blue-50 border-blue-400'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{method.name}</p>
                              {method.description && (
                                <p className="text-sm text-gray-500">{method.description}</p>
                              )}
                            </div>
                            {formData.paymentMethodId === method.id && (
                              <Check className="w-5 h-5 text-teal-600" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <DashboardLayout userType="client" title="Nouvelle commande">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((s, i) => (
            <div key={s.key} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  i < currentStepIndex
                    ? 'bg-blue-600 text-white'
                    : i === currentStepIndex
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {i < currentStepIndex ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`ml-2 text-sm font-medium hidden sm:block ${
                i <= currentStepIndex ? 'text-gray-900' : 'text-gray-400'
              }`}>
                {s.label}
              </span>
              {i < steps.length - 1 && (
                <div className={`w-12 sm:w-20 h-0.5 mx-2 ${
                  i < currentStepIndex ? 'bg-blue-600' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <Card className="mb-6">
        {renderStepContent()}
      </Card>

      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => {
            const prevStepIndex = currentStepIndex - 1;
            if (prevStepIndex >= 0) {
              setStep(steps[prevStepIndex].key);
            } else {
              navigate('/dashboard/client');
            }
          }}
          leftIcon={<ChevronLeft className="w-4 h-4" />}
        >
          {currentStepIndex === 0 ? 'Annuler' : 'Retour'}
        </Button>

        {step === 'payment' ? (
          <Button
            onClick={handleSubmit}
            isLoading={submitting}
            disabled={!canProceed()}
            rightIcon={<Check className="w-4 h-4" />}
          >
            {formData.payNow ? 'Créer et payer' : 'Créer la commande'}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            rightIcon={<ChevronRight className="w-4 h-4" />}
          >
            Suivant
          </Button>
        )}
      </div>
    </DashboardLayout>
  );
}
